import { useCallback, useState } from 'react';
import { apiService } from '../../../services/apiService';
import { DashboardJob, JobKind } from '../jobModel';

export type BusyAction = 'download' | 'delete';

const DOWNLOAD: Record<JobKind, (jobId: string) => Promise<unknown>> = {
  export: (id) => apiService.downloadExportZip(id),
  label: (id) => apiService.downloadLabels(id),
  mapping: (id) => apiService.downloadMappingDocumentJob(id),
};

const DELETE_ONE: Record<JobKind, (jobId: string) => Promise<unknown>> = {
  export: (id) => apiService.deleteExportJob(id),
  label: (id) => apiService.deleteLabelJob(id),
  mapping: (id) => apiService.deleteMappingDocumentJob(id),
};

// Export jobs have a bulk endpoint; label and mapping jobs are deleted one by one.
function deleteRequests(jobs: DashboardJob[]): Promise<unknown>[] {
  const exportIds = jobs.filter((j) => j.kind === 'export').map((j) => j.id);
  const requests = jobs.filter((j) => j.kind !== 'export').map((j) => DELETE_ONE[j.kind](j.id));
  if (exportIds.length === 1) requests.push(DELETE_ONE.export(exportIds[0]));
  else if (exportIds.length > 1) requests.push(apiService.deleteMultipleExportJobs(exportIds));
  return requests;
}

/** Download/delete side effects plus per-job busy state for row spinners. */
export function useJobActions(refetch: () => Promise<void>) {
  const [busy, setBusy] = useState<Readonly<Record<string, BusyAction>>>({});

  const mark = useCallback((ids: string[], action: BusyAction | null) => {
    setBusy((prev) => {
      const next = { ...prev };
      ids.forEach((id) => {
        if (action) next[id] = action;
        else delete next[id];
      });
      return next;
    });
  }, []);

  /** Downloads one job at a time (each triggers a browser save); resolves to the failure count. */
  const download = useCallback(
    async (jobs: DashboardJob[]): Promise<number> => {
      let failures = 0;
      for (const job of jobs) {
        mark([job.id], 'download');
        try {
          await DOWNLOAD[job.kind](job.id);
        } catch (error) {
          failures += 1;
          console.error(`Failed to download ${job.kind} job ${job.id}:`, error);
        } finally {
          mark([job.id], null);
        }
      }
      return failures;
    },
    [mark],
  );

  /** Deletes in parallel, then refetches; resolves to the number of failed requests. */
  const remove = useCallback(
    async (jobs: DashboardJob[]): Promise<number> => {
      const ids = jobs.map((j) => j.id);
      mark(ids, 'delete');
      const results = await Promise.allSettled(deleteRequests(jobs));
      let failures = 0;
      results.forEach((r) => {
        if (r.status === 'rejected') {
          failures += 1;
          console.error('Failed to delete job(s):', r.reason);
        }
      });
      await refetch();
      mark(ids, null);
      return failures;
    },
    [mark, refetch],
  );

  return { busy, download, remove };
}
